import React, { useState, useEffect } from 'react';
import { useParams, useHistory, Prompt } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Spinner, Form } from 'react-bootstrap';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { toast } from 'sonner';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import { getSelectedCompanyType } from '../../../../store/selectors/AuthSelectors';
import { normalizeSSN, validateSSNDate, validateSSNChecksum } from '../../../../utils/ssnUtils';
// import { validateClearingNumber, validateAccountNumber, getBankInfo } from '../../../../utils/bankAccountUtils'; // Temporarily commented out
import PageHeader from '../../../layouts/PageHeader';
import Callout from '../../Common/Callout';
import InfoPopover from '../../Common/InfoPopover';
import { brandSelectTheme } from '../../Common/reactSelectTheme';
import { Save, AlertTriangle, X, UserPlus } from 'lucide-react';
import './new-employee.css';

// react-select sized like the text controls on this page (34px, hairline
// border) — the same numbers the invoice editor's selects use.
const selectControl = (borderColor) => (base) => ({
  ...base, minHeight: 34, borderRadius: 8, borderColor, fontSize: '0.875rem',
});
const selectStyles = {
  control: selectControl('#d1d5db'),
  menuPortal: (base) => ({ ...base, zIndex: 1060 }),
};
const invalidSelectStyles = { ...selectStyles, control: selectControl('#dc3545') };

// Kept outside the page component so the input does not remount (and lose
// focus) on every keystroke.
const InputField = ({
  label,
  name,
  type = 'text',
  required = false,
  placeholder = '',
  // Explanation shown in the green popover on hover/focus of an "i" icon
  // next to the label — never as a line of text under the control.
  hint = null,
  hintTitle = null,
  options = null,
  formik,
  showValidation
}) => {
  const invalid = showValidation && formik.touched[name] && formik.errors[name];
  const controlClass = `emp-form__control${invalid ? ' is-invalid' : ''}`;
  const id = `emp-${name}`;
  return (
    <div className="emp-form__field">
      <label className="emp-form__label" htmlFor={id}>
        {label}
        {required && <span className="emp-form__required" aria-hidden="true">*</span>}
        {hint && <InfoPopover title={hintTitle} ariaLabel={`Om ${label}`}>{hint}</InfoPopover>}
      </label>
      {options ? (
        <Select
          inputId={id}
          name={name}
          options={options}
          value={options.find((option) => option.value === formik.values[name]) || null}
          onChange={(option) => formik.setFieldValue(name, option ? option.value : '')}
          onBlur={() => formik.setFieldTouched(name, true)}
          placeholder={placeholder || 'Välj…'}
          isSearchable={false}
          theme={brandSelectTheme}
          styles={invalid ? invalidSelectStyles : selectStyles}
          classNamePrefix="react-select"
          menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
        />
      ) : (
        <Form.Control
          id={id}
          type={type}
          name={name}
          value={formik.values[name]}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder={placeholder}
          className={controlClass}
        />
      )}
      {invalid && (
        <div className="emp-form__error">
          <AlertTriangle size={14} />
          <span>{formik.errors[name]}</span>
        </div>
      )}
    </div>
  );
};

const EMPLOYEE_ROLE_OPTIONS = [
  { value: 'regular', label: 'Vanlig anställd (BAS 7210)' },
  { value: 'company_director', label: 'Företagsledare (BAS 7220)' },
  { value: 'board_member', label: 'Styrelseledamot — arvode (BAS 7240)' },
];

const NewEmployee = () => {
  const { companyId } = useParams();
  const history = useHistory();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccessfullySubmitted, setIsSuccessfullySubmitted] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  // const [identifiedBank, setIdentifiedBank] = useState(null); // Temporarily commented out

  // Surface an Anställningsroll selector for any juridisk person that can
  // have styrelse / VD-roll requiring BAS class 72 routing (7220 företags-
  // ledare, 7240 styrelsearvoden). Per BAS-praxis this includes
  // Aktiebolag (ABL 8 kap), Ekonomisk förening (LEF 7 kap), Ideell
  // förening (case-law board structure), and Stiftelse (SL 2 kap
  // styrelseledamot). HB/KB/EF use partner / owner constructs (resp.
  // bolagsman / näringsidkare), which aren't employee-roles; for those
  // the selector stays hidden. Backend `_buildSalaryDebits` is universally
  // composite (not gated on companyType), so seeded charts that lack
  // 7220/7240 collapse to 7210 — see reference_bas_salary_routing.md.
  const companyType = useSelector(getSelectedCompanyType);
  const ROLE_SELECTOR_TYPES = new Set([
    'Aktiebolag',
    'Ekonomisk förening',
    'Ideell förening',
    'Stiftelse',
  ]);
  const showRoleSelector = ROLE_SELECTOR_TYPES.has(companyType);

  const formik = useFormik({
    initialValues: {
      // Employee Details
      firstname: '',
      lastname: '',
      ssn: '',
      jobtitle: '',
      clearingnumber: '',
      bankaccountnumber: '',
      phonenumber: '',
      email: '',
      address: '',
      postnummer: '',
      ort: '',
      active: true,
      employee_role: 'regular',
    },
    validationSchema: Yup.object({
      // Employee Details Validation
      firstname: Yup.string().required('Förnamn är obligatoriskt'),
      lastname: Yup.string().required('Efternamn är obligatoriskt'),
      ssn: Yup.string()
        .test('ssn-format', 'Personnummer måste vara i format ÅÅMMDD-XXXX eller ÅÅÅÅMMDD-XXXX',
          value => {
            if (!value) return false;
            const clean = value.replace(/\D/g, '');
            return clean.length === 10 || clean.length === 12;
          }
        )
        .test('ssn-date', 'Personnummer innehåller ett ogiltigt datum',
          value => {
            if (!value) return false;
            return validateSSNDate(value);
          }
        )
        .test('ssn-checksum', 'Ogiltigt personnummer (kontrollsiffran stämmer inte)',
          value => {
            if (!value) return false;
            return validateSSNChecksum(value);
          }
        )
        .required('Personnummer är obligatoriskt'),
      // jobtitle: Yup.string().required('Jobbtitel är obligatoriskt'), // Temporarily commented out
      // clearingnumber: Yup.string() // Temporarily commented out
      //   .test('clearing-format', 'Clearingnummer måste vara 4-5 siffror',
      //     value => {
      //       if (!value) return false;
      //       return /^\d{4,5}$/.test(value.replace(/[\s-]/g, ''));
      //     }
      //   )
      //   .test('clearing-valid', function (value) {
      //     if (!value) return true;
      //     const result = validateClearingNumber(value);
      //     return result.valid || this.createError({ message: result.message });
      //   })
      //   .required('Clearingnummer är obligatoriskt'),
      // bankaccountnumber: Yup.string() // Temporarily commented out
      //   .test('account-format', 'Kontonummer måste vara 6-10 siffror',
      //     value => {
      //       if (!value) return false;
      //       const clean = value.replace(/[\s-]/g, '');
      //       return /^\d{6,10}$/.test(clean);
      //     }
      //   )
      //   .test('account-valid', function (value) {
      //     if (!value) return true;
      //     const clearing = this.parent.clearingnumber;
      //     if (!clearing) return true; // Skip if clearing not entered yet
      //     const result = validateAccountNumber(clearing, value);
      //     return result.valid || this.createError({ message: result.message });
      //   })
      //   .required('Bankkonto är obligatoriskt'),
      phonenumber: Yup.string()
        .matches(/^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/, 'Ogiltigt telefonnummer')
        .required('Telefonnummer är obligatoriskt'),
      email: Yup.string().email('Ogiltig e-postadress').required('E-post är obligatoriskt'),
      address: Yup.string().required('Adress är obligatoriskt'),
      postnummer: Yup.string()
        .test('postal-code-format', 'Postnummer måste vara i format XXXXX eller XXX XX',
          value => {
            if (!value) return false;
            // Remove spaces and check if it's 5 digits
            const cleaned = value.replace(/\s/g, '');
            return /^\d{5}$/.test(cleaned);
          }
        )
        .required('Postnummer är obligatoriskt'),
      ort: Yup.string().required('Ort är obligatoriskt'),
    }),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      setShowValidation(true);

      try {
        const createEmployeeApiUrl = getApiUrl(`${companyId}/hr/employees`);

        // Normalize SSN and postal code before sending
        const employeeDetails = {
          ...values,
          ssn: normalizeSSN(values.ssn),
          postnummer: values.postnummer.replace(/\s/g, '') // Remove spaces from postal code
        };

        const response = await axios.post(
          createEmployeeApiUrl,
          { employeeDetails },
          { withCredentials: true }
        );

        if (response.data.status === 'success') {
          setIsSuccessfullySubmitted(true);
          toast.success('Den anställda har skapats');
          history.push(`/${companyId}/employees`);
        }
      } catch (error) {
        console.error('Error:', error);
        const data = error.response?.data;
        // The validation middleware answers with { error, details: [{ field, message }] };
        // surface the first field message rather than the generic fallback.
        const errorMessage = data?.message
          || data?.details?.[0]?.message
          || 'Ett fel inträffade vid sparande av anställd.';
        toast.error(errorMessage);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  // Handle form dirty state for navigation warnings
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (formik.dirty && !isSuccessfullySubmitted) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [formik.dirty, isSuccessfullySubmitted]);

  // Identify bank when clearing number changes (only when complete)
  // Temporarily commented out since bank account fields are hidden
  // useEffect(() => {
  //   const clearingValue = formik.values.clearingnumber;
  //   if (clearingValue) {
  //     const cleanValue = clearingValue.replace(/\D/g, '');
  //     if (cleanValue.length >= 4 && cleanValue.length <= 5) {
  //       const bankInfo = getBankInfo(clearingValue);
  //       if (bankInfo) {
  //         setIdentifiedBank(bankInfo.bank);
  //       } else {
  //         setIdentifiedBank(null);
  //       }
  //     } else {
  //       setIdentifiedBank(null);
  //     }
  //   } else {
  //     setIdentifiedBank(null);
  //   }
  // }, [formik.values.clearingnumber]);

  const handleCancel = async () => {
    if (formik.dirty && !isSuccessfullySubmitted) {
      const result = await Swal.fire({
        title: 'Är du säker?',
        text: 'Du har osparade ändringar som kommer att gå förlorade!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#717579',
        confirmButtonText: 'Ja, avbryt!',
        cancelButtonText: 'Nej, fortsätt redigera'
      });

      if (result.isConfirmed) {
        history.push(`/${companyId}/employees`);
      }
    } else {
      history.push(`/${companyId}/employees`);
    }
  };

  const isFormReadyToSubmit = () => {
    const requiredFields = [
      'firstname', 'lastname', 'ssn', // 'jobtitle', 'clearingnumber', 'bankaccountnumber', // Temporarily removed
      'phonenumber', 'email', 'address',
      'postnummer', 'ort'
    ];

    // Check all required fields are filled
    const allFieldsFilled = requiredFields.every(field => {
      const value = formik.values[field];
      return value !== null && value !== undefined && value !== '';
    });

    // Check for validation errors
    const noErrors = Object.keys(formik.errors).length === 0;

    return allFieldsFilled && noErrors;
  };


  const hasErrors = Object.keys(formik.errors).length > 0;

  return (
    <>
      <Prompt
        when={formik.dirty && !isSuccessfullySubmitted}
        message="Du har osparade ändringar. Är du säker på att du vill lämna?"
      />

      <PageHeader
        backTo={{ to: `/${companyId}/employees`, label: 'Tillbaka till personal' }}
        icon={UserPlus}
        title="Lägg till anställd"
        subtitle="Skapa en ny anställd med grundläggande personuppgifter. Anställningskontrakt skapas separat."
      />

      <Form onSubmit={formik.handleSubmit} className="emp-form" noValidate>
        <Callout tone="info">
          Obligatoriska fält är markerade med *. Efter att anställd är skapad kan du skapa anställningskontrakt separat. All information hanteras säkert enligt GDPR.
        </Callout>

        {showValidation && hasErrors && (
          <Callout tone="danger" role="alert">
            <strong>Åtgärda följande fel innan du fortsätter:</strong>
            <ul className="mb-0 mt-1 ps-3">
              {Object.values(formik.errors).map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </Callout>
        )}

        {/* Personal Information */}
        <section className="emp-form__card">
          <div className="emp-form__card-head">
            <h2 className="emp-form__card-title">Personuppgifter</h2>
          </div>

          <div className="emp-form__grid">
            <InputField
              label="Förnamn"
              name="firstname"
              required={true}
              placeholder="Ange förnamn"
              formik={formik}
              showValidation={showValidation}
            />

            <InputField
              label="Efternamn"
              name="lastname"
              required={true}
              placeholder="Ange efternamn"
              formik={formik}
              showValidation={showValidation}
            />

            <InputField
              label="Personnummer"
              name="ssn"
              required={true}
              placeholder="ÅÅMMDDXXXX"
              formik={formik}
              showValidation={showValidation}
            />

            {/* Temporarily commented out - will be added back in the future */}
            {/* <InputField
              label="Jobbtitel"
              name="jobtitle"
              required={true}
              placeholder="Ange jobbtitel"
              formik={formik}
              showValidation={showValidation}
            /> */}

            {showRoleSelector && (
              <div data-testid="employee-role-selector">
                <InputField
                  label="Anställningsroll"
                  name="employee_role"
                  options={EMPLOYEE_ROLE_OPTIONS}
                  hintTitle="Anställningsroll"
                  hint="Styr vilket lönekonto som debiteras i lönekörningens verifikat: 7210 för vanlig anställd, 7220 för företagsledare (ABL 8 kap.) och 7240 för styrelsearvode."
                  formik={formik}
                  showValidation={showValidation}
                />
              </div>
            )}
          </div>
        </section>

        {/* Contact Information */}
        <section className="emp-form__card">
          <div className="emp-form__card-head">
            <h2 className="emp-form__card-title">Kontaktuppgifter</h2>
          </div>

          <div className="emp-form__grid">
            <InputField
              label="E-postadress"
              name="email"
              type="email"
              required={true}
              placeholder="namn@exempel.se"
              formik={formik}
              showValidation={showValidation}
            />

            <InputField
              label="Telefonnummer"
              name="phonenumber"
              type="tel"
              required={true}
              placeholder="+46 XX XXX XX XX"
              formik={formik}
              showValidation={showValidation}
            />
          </div>
        </section>

        {/* Address Information */}
        <section className="emp-form__card">
          <div className="emp-form__card-head">
            <h2 className="emp-form__card-title">Adressuppgifter</h2>
          </div>

          <div className="emp-form__grid">
            <InputField
              label="Adress"
              name="address"
              required={true}
              placeholder="Gatunamn XX"
              formik={formik}
              showValidation={showValidation}
            />

            <InputField
              label="Postnummer"
              name="postnummer"
              required={true}
              placeholder="XXXXX"
              formik={formik}
              showValidation={showValidation}
            />

            <InputField
              label="Ort"
              name="ort"
              required={true}
              placeholder="Ortnamn"
              formik={formik}
              showValidation={showValidation}
            />
          </div>
        </section>

        {/* Bank Information - Temporarily commented out (available in employee detail view) */}
        {/* <section className="emp-form__card">
          <div className="emp-form__card-head">
            <h2 className="emp-form__card-title">Bankuppgifter</h2>
          </div>

          <div className="emp-form__grid">
            <div>
              <InputField
                label="Clearingnummer"
                name="clearingnumber"
                required={true}
                placeholder="XXXX"
                formik={formik}
                showValidation={showValidation}
              />
              {identifiedBank && !formik.errors.clearingnumber && formik.values.clearingnumber && (
                <div className="emp-form__card-hint text-success d-flex align-items-center gap-1">
                  <CheckCircle size={12} />
                  <span>{identifiedBank}</span>
                </div>
              )}
            </div>

            <InputField
              label="Kontonummer"
              name="bankaccountnumber"
              required={true}
              placeholder="XXXXXXXXX"
              formik={formik}
              showValidation={showValidation}
            />
          </div>
        </section> */}

        {/* Status */}
        <section className="emp-form__card">
          <div className="emp-form__card-head">
            <h2 className="emp-form__card-title">Status</h2>
            <p className="emp-form__card-hint">Inaktiva anställda visas inte i lönekörningen.</p>
          </div>

          <div className="emp-form__check">
            <Form.Check
              type="checkbox"
              id="emp-active"
              name="active"
              checked={formik.values.active}
              onChange={formik.handleChange}
            />
            <label className="emp-form__check-label" htmlFor="emp-active">
              Aktiv anställd
            </label>
          </div>
        </section>

        {/* Sticky action bar */}
        <div className="emp-form__actions-wrap">
          <div className="emp-form__actions" role="region" aria-label="Åtgärder">
            <div className="emp-form__actions-note">* Obligatoriska fält</div>
            <div className="emp-form__actions-buttons">
              <button
                type="button"
                className="ll-btn ll-btn--secondary"
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                <X strokeWidth={2} />
                Avbryt
              </button>
              <button
                type="submit"
                className="ll-btn ll-btn--primary"
                disabled={isSubmitting || !isFormReadyToSubmit()}
              >
                {isSubmitting ? (
                  <>
                    <Spinner animation="border" size="sm" role="status" aria-hidden="true" />
                    Sparar...
                  </>
                ) : (
                  <>
                    <Save strokeWidth={2} />
                    Skapa anställd
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </Form>
    </>
  );
};

export default NewEmployee;
